class Helper {

  static getAPIState = ({ itemFetchId, productData, collectionFetchId, collectionData } = {}) => {
    const { data, loading, error } = (collectionFetchId ? collectionData : productData) || {};
    const fetchId = itemFetchId || collectionFetchId;

    if (error && !data) return 'error';
    if (!data || (fetchId !== data?.product?.itemId && !data?.collection)) return 'loading';
    if (data && loading) {
      const { product } = data || {};
      const { itemId, media } = product || {};
      const { augmentedRealityLink, images, threeSixty, video } = media || {};
      const hasMedia = images?.length || video?.length || threeSixty?.length || augmentedRealityLink;
      if (hasMedia && fetchId === itemId) {
        return 'partial';
      }
      return 'loading';
    }
    return 'ready';
  };

  static AR_URL = 'https://assets.thdstatic.com/mobile-apps/2d/images/';

  static getStructuredMediaList = ({ mediaList, size = '145', isThumbnail = false }) => {
    return (mediaList || []).map((media) => {
      const { sizes, type, url, videoStill } = media;
      let newUrl = url;
      let newVideoStill = videoStill;
      if (type.match(/IMAGE/ig)) {
        if ((sizes || []).indexOf(size) > -1) {
          newUrl = url.replace(/<SIZE>/ig, size);
        } else if (url && !url.match(/<SIZE>/ig)) {
          // This is meant for Akamai media images that handle auto-sizing on their own
          if (isThumbnail) {
            const mediaUrl = new URL(url);
            mediaUrl.searchParams.set('impolicy', 'thumbnail');
            newUrl = mediaUrl.toString();
          }
        } else {
          return null;
        }
      } else if (type.match(/VIDEO/ig)) {
        if (!url) {
          return null;
        }
        if (!media.videoStill) {
          newVideoStill = 'https://images.thdstatic.com/catalog/productImages/No_Image_145.jpg';
        }
      }
      return {
        ...media,
        url: newUrl,
        videoStill: newVideoStill
      };
    }).filter((media) => !!media
      && (
        Helper.isImage({ media })
        || Helper.isAR({ media })
        || Helper.isVideo({ media })
        || Helper.isImage360({ media })
      ));
  };

  /** *
   * Takes the initial media object containing the 3 media types and returns the array of media items
   * @param media
   * @returns {*[]}
   */
  static getInitialMediaList = ({ media, externalMedia }) => {
    if (externalMedia) {
      return externalMedia;
    }
    const { augmentedRealityLink, images, threeSixty, video } = media || {};
    const videos = (video || []).map((v) => {
      return {
        ...v,
        id: v.videoId
      };
    });
    const augmentedReality = !(augmentedRealityLink?.image && augmentedRealityLink?.usdz) ? [] : [{
      ...augmentedRealityLink,
      mediaStill: `${Helper.AR_URL}${augmentedRealityLink.image}`,
      type: 'AUGMENTED_REALITY'
    }];
    const imagesWithIds = (images || []).map((image, imageIndex) => {
      return {
        ...image,
        id: `${image.url.split('/').pop().split('_')[0]}.${imageIndex}`
      };
    });

    // determine the start position for videos or 360 images
    const position = Math.min(imagesWithIds.length || 0, 2);
    const restOfImages = imagesWithIds.splice(position);
    // TODO: remove when type is added from API team
    const threeSixtyWithType = (threeSixty || []).map((item) => {
      return {
        ...item,
        mediaStill: `${item.url}/?thumb&profile=600`,
        type: 'THREESIXTY'
      };
    });
    return [...imagesWithIds, ...videos, ...(threeSixtyWithType || []), ...augmentedReality, ...restOfImages];

    // if (arAsset) {
    //   mediaList.push(arAsset)
    // }
    // return mediaList;
  };

  static isVideo = ({ media = {} }) => {
    const { type } = media;
    return /VIDEO/i.test(type);
  };

  static isImage360 = ({ media = {} }) => {
    const { type } = media;
    return type === 'THREESIXTY';
  };

  static isImage = ({ media = {} }) => {
    const { type } = media;
    return /IMAGE/i.test(type);
  };

  static isAR = ({ media = {} }) => {
    const { type } = media;
    return type === 'AUGMENTED_REALITY';
  };

  static normalizeImage = ({ image, size }) => {
    const { sizes, type, url } = image;
    const imageSize = sizes.indexOf(size) > -1 ? size : sizes.pop();
    return {
      ...image,
      mediaType: type,
      id: url.split('/').pop().split('_')[0],
      url: url.replace('<SIZE>', imageSize)
    };
  };

  // TODO: Check if this is still needed after AR asset is returned by API
  static normalizeARAsset = ({ product = {}, imageSize = '' }) => {
    const { augmentedRealityLink = [] } = product.media;

    let arAsset = augmentedRealityLink;
    // Normalize AR asset to match media list objects
    if (arAsset) {
      const usdz = arAsset.usdz && arAsset.usdz.length && arAsset.usdz[0];
      const assetLocation = arAsset.image;
      if (assetLocation && usdz) {
        const id = assetLocation.split('_')[0] || null;
        arAsset = {
          id,
          url: Helper.AR_URL + assetLocation,
          usdz,
          isAR: true,
          height: imageSize,
          type: 'AUGMENTED_REALITY'
        };
        return [arAsset];
      }
    }
    return [];
  };

  static isTouchEnabled = () => {
    if (typeof window !== 'undefined') {
      return ('ontouchstart' in window)
        || (navigator.maxTouchPoints > 0)
        || (navigator.msMaxTouchPoints > 0);
    }
    return false;
  }

  static getHighResImages(media, axios) {
    const { id, url } = media || {};
    if (id && url && this.isImage({ media })) {
      const locationArray = url.split('/');
      const fileName = locationArray.pop();
      const extension = fileName.substr(fileName.lastIndexOf('.') + 1);
      const highResImage = {
        ...media,
        url: `${locationArray.join('/')}/${id}_max.${extension}`
      };
      return axios.head(highResImage.url, {}).then(() => {
        return highResImage;
      }).catch(() => Promise.resolve(false));
    }
    return Promise.resolve(false);
  }

  static formatVideoProps(mainAsset) {
    return {
      source: mainAsset.source === 'CONFIGURABLE_S3' ? mainAsset.source : null,
      videoSource: mainAsset.source === 'CONFIGURABLE_S3' ? mainAsset.url : null,
      videoId: mainAsset.source === 'CONFIGURABLE_S3' ? null : mainAsset.videoId
    };
  }

  static getDisplayedMedia = ({
    isMobile, itemId = null, productData, collectionId = null, collectionData, externalMedia = null, overrideData = null, placeholder = null
  }) => {
    const displayedMediaReturn = { displayedMedia: {}, apiState: '', returnStatement: null };
    const { media: overrideMedia } = overrideData || {};

    const apiState = overrideMedia ? null : this.getAPIState({
      itemFetchId: itemId, productData, collectionFetchId: collectionId, collectionData
    });

    if (apiState === 'loading') {
      displayedMediaReturn.apiState = apiState;
      displayedMediaReturn.returnStatement = placeholder;
    } else {
      // Construct displayedMedia
      const { product } = productData.data || {};
      const { collection } = collectionData?.data || {};

      const mediaSource = (collectionId ? collection : product)?.media;
      const mediaList = this.getInitialMediaList({ media: overrideMedia || mediaSource, externalMedia });

      if (apiState === 'error' || !mediaList?.length) {
        displayedMediaReturn.apiState = apiState;
        displayedMediaReturn.returnStatement = null;
      } else {
        const thumbnailSize = isMobile ? '100' : '145';
        const thumbnails = this.getStructuredMediaList({ mediaList, size: thumbnailSize, isThumbnail: true });
        const main = this.getStructuredMediaList({ mediaList, size: '600' });
        const zoom = this.getStructuredMediaList({ mediaList, size: '1000' });
        displayedMediaReturn.displayedMedia = {
          thumbnails,
          main,
          zoom
        };
      }
    }

    return displayedMediaReturn;
  };

  // for swatch Hover
  static getMainHover = ({
    hoveredProductResponse = null, collection = null, overrideData = null, externalMedia = null
  }) => {
    const { media: overrideMedia } = overrideData || {};
    const mediaSourceHover = (collection || hoveredProductResponse?.data?.product)?.media;
    const mediaListHover = this.getInitialMediaList({ media: overrideMedia || mediaSourceHover, externalMedia });
    const mainHover = this.getStructuredMediaList({ mediaList: mediaListHover, size: '600' });
    return mainHover;
  };
}

export default Helper;